import React, { useState, useRef } from 'react'
import './SliderBar.css'
import { wrap } from 'lodash'

export interface labelsDescriptionArrayProps {
    label: string,
    description: string
}
export interface SliderProps {
    labelsDescriptionArray: Array<labelsDescriptionArrayProps>
    value?: number
    advanceSlider: boolean
    simpleSliderDefaultValue?: number
    onChange: (arg: number) => void
    discrete?: boolean
    start?: number
    end?: number
    step?: number
    fill?: string
    background?: string
    marks?: boolean
    markForEachStep?: boolean
    showValueTooltip?: boolean
    showDescription?: boolean
    makeLabelGLow?: boolean

}

const SliderBar = (props: SliderProps) => {

    if (props.advanceSlider) {
        return <AdvanceSliderBar {...props} />
    } else {
        return <SimpleSliderBar {...props} />
    }
}

const SimpleSliderBar = (props: SliderProps) => {



    const { start = 0, end = 100, simpleSliderDefaultValue = 0, step = 1, fill = '#6C5CE7', background = "#C7B9FA", onChange } = props
    const [value, setValue] = useState(simpleSliderDefaultValue)

    const onSlide = event => {
        const newValue = event.target.value
        setValue(newValue)
        if (onChange) {
            onChange(newValue)
        }
    }

    const settings = {
        fill: fill || '#A9B6CB',
        background: background || '#EDF1F7',
    }

    const percentage = (100 * (value - start)) / (end - start)

    const bg = `linear-gradient(90deg, ${settings.fill} ${percentage}%, ${settings.background} ${percentage + 0.1
        }%)`

    return (
        <div className="SliderBar">
            <input
                type="range"
                min={start}
                max={end}
                className="SliderBar__track"
                style={{ background: bg, color: `${settings.fill}` }}
                value={value}
                onChange={onSlide}
                step={step}
            />
            <div className="SliderBar__numbers flex">
                <span className="SliderBar__start-number">{start}</span>
                <span className="SliderBar__start-number">{end}</span>
            </div>
        </div>
    )
}

const AdvanceSliderBar = (props: SliderProps) => {

    const { start = 0, end = 100, step = 1, onChange, value = 0, labelsDescriptionArray = [
        { label: "0", description: "" },
        { label: "100", description: "" }
    ], discrete = false, marks = false, markForEachStep = false, showValueTooltip = false, showDescription = false } = props

    const sliderRef = useRef<HTMLInputElement>(null)
    const [isHoveredOrActive, setIsHoveredOrActive] = useState(false);


    const labelDescriptionArray: labelsDescriptionArrayProps[] = labelsDescriptionArray;

    // Edge cases
    if (labelDescriptionArray.length <= 1) {
        // Return fallback UI
        return <div className='Advance-SlideBar-fallback-UI'>No objects in labels Array provided, Provide at least 2 objects</div>;
    }
    else if (step <= 0 || !step) {
        return <div className='Advance-SlideBar-fallback-UI'>Step value cannot be {step}, Provide a valid step value</div>;
    }
    else if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end || start < 0 || end <= 0) {
        return <div className='Advance-SlideBar-fallback-UI'>{start === 0 ? null : `Start value cannot be ${start} and `} End value cannot be {end}, Provide a valid start and end value</div>;
    }

    const onSlide = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(Math.round(Number(event.target.value)).toFixed(2));
        if (onChange) {
            onChange(newValue)
        }
    }

    const settings = {
        fill: props.fill || '#6C5CE7', //dark
        background: props.background || '#C7B9FA', //light
    }

    const percentage = (100 * (value - start)) / (end - start)

    const bg = `linear-gradient(90deg, ${settings.fill} ${percentage}%, ${settings.background} ${percentage + 0.1
        }%)`

    const newValueAndPositionLogicForLabelAndMark = (index: number): { newValue: number, newPosition: number } => {
        // Here we are calculating the value of each label and mark
        const newLabelAndMarksValueLogic: number = (end - start) / (labelDescriptionArray.length - 1);
        // Here we are calculating value with respect to the position of the label and mark
        const newLabelAndMarksValuePositionLogic: number = (start + (newLabelAndMarksValueLogic) * index);
        // Here we are rounding the value to the nearest integer
        const newValue: number = Number(Math.round(newLabelAndMarksValuePositionLogic));
        // Mapping the value to a percentage
        const newPosition: number = Number(Math.round((newLabelAndMarksValuePositionLogic / end) * 100));
        return { newValue, newPosition };
    }

    const handleMarkAndLabelClick = (index: number) => {
        const { newValue } = newValueAndPositionLogicForLabelAndMark(index);
        if (onChange) {
            onChange(newValue);
        }
    };

    const labelsMapper = labelDescriptionArray.map((item: labelsDescriptionArrayProps, index: number) => {
        const { newPosition } = newValueAndPositionLogicForLabelAndMark(index);
        const labelPosition = newPosition;
        return (
            <span
                key={index}
                className='Advance-SliderBar-labels'
                lang='en'
                style={{
                    left: `${(labelPosition).toFixed(2)}%`,
                    color: `${props.makeLabelGLow ? (labelPosition == value ? `${settings.fill}` : '#212121') : '#212121'}`,
                    fontWeight: `${props.makeLabelGLow ? (labelPosition == value ? '600' : 'normal') : 'normal'}`
                }}
                onClick={() => { handleMarkAndLabelClick(index) }}
            >
                {item.label}
            </span>
        )
    });

    const customThumbPositionLogicAndStyles = () => {
        const positionFromStartToValue = (value - start);
        const totalRange = (end - start);
        return {
            left: `${((positionFromStartToValue) / (totalRange)) * 100}%`,
            background: `${settings.fill}`,
        }
    };

    const discreteStep = (100 / (labelDescriptionArray.length - 1));
    const stepCalculator = (Number((discrete ? (discreteStep) : step).toFixed(2)));

    const handleMouseDown = () => {
        setIsHoveredOrActive(true);
        // Add new event listeners
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        setIsHoveredOrActive(true);
        if (!sliderRef.current) {
            return;
        }
        // Get the slider element
        const slider = sliderRef.current;
        // Get the slider's bounding rectangle
        const rect = slider.getBoundingClientRect();
        // Calculate the x position of the mouse relative to the slider
        // here e.clientX is the x position of the mouse relative to the window
        // And rect.left is the x position of the slider relative to the window
        const x = e.clientX - rect.left;
        // Calculate the width of the slider
        const width = rect.right - rect.left;
        // Calculate the new value of the slider by converting the x position to a value within the range of the slider
        // here (x / width) gives the percentage of the slider that the mouse is over
        // and (end - start) gives the range of the slider
        // and ((end - start) * (x / width)) gives the value within the range of the slider
        // and start + ((end - start) * (x / width)) gives the value within the range of the slider starting from the start value
        let newValue = Number(Math.round(((x / width) * (end - start)) + start).toFixed(2));

        // This logic is used to snap the thumb to the nearest step
        if (discrete) {
            // Calculate the nearest step
            const stepInNumber = stepCalculator;
            // Calculate the remainder which is used to determine if the thumb should snap to the next step
            const remainder = newValue % stepInNumber;
            // If the remainder is less than half of the step, snap to the lower step, otherwise snap to the higher step
            if (remainder < stepInNumber / 2) {
                // Snap to the lower step if remainder is less than half of the step
                newValue = newValue - remainder;
            } else {
                // Snap to the higher step if remainder is greater than half of the step
                newValue = newValue + stepInNumber - remainder;
            }
        }
        //Ensure the value is within the range
        newValue = Math.round(Math.max(start, Math.min(end, newValue)));
        // Update the value
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleMouseUp = () => {
        setIsHoveredOrActive(false);
        // Remove the event listeners
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };


    const marksMapper = labelDescriptionArray.map((_item: labelsDescriptionArrayProps, index: number) => {
        const { newPosition } = newValueAndPositionLogicForLabelAndMark(index);
        const markLeftPosition = newPosition;
        return (
            <span
                key={index}
                className='Advance-SliderBar-mark'
                style={{
                    left: `${(markLeftPosition).toFixed(2)}%`,
                    backgroundColor: markLeftPosition <= value ? props.background || '#c7b9fa' : props.fill || '#5d50bf'
                }}
                onClick={() => { handleMarkAndLabelClick(index) }}
            ></span>
        )
    });

    const range = (end - start);
    const totalSteps = range / stepCalculator;
    const stepsArray = Array.from({ length: totalSteps + 1 }, (_, i) => i * stepCalculator);
    const marksForEachStepMapper = stepsArray.map((currentStep) => {
        // Calculate the left position of the current mark as a percentage of the total range.
        // The Math.min function is used to ensure that the left position never exceeds 100%.
        const markLeftPosition = Math.min(((currentStep) / range) * 100, 100);
        const topValue = stepsArray.length <= 8 ? '-0.62rem' : stepsArray.length <= 50 ? '-0.62rem' : '-0.62rem';
        const heightValue = stepsArray.length <= 8 ? '0.188rem' : stepsArray.length <= 50 ? '0.188rem' : '0.188rem';
        const widthValue = stepsArray.length <= 8 ? '0.188rem' : stepsArray.length <= 50 ? '0.188rem' : '0.188rem'
        return (
            <span
                key={currentStep}
                id={currentStep.toString()}
                className='Advance-SliderBar-mark-for-each-step'
                style={{
                    top: `${topValue}`,
                    height: `${heightValue}`,
                    width: `${widthValue}`,
                    left: `${(markLeftPosition).toFixed(2)}%`,
                    backgroundColor: currentStep + start <= (value) ? props.background || '#c7b9fa' : props.fill || '#5d50bf'
                }}
                onClick={() => {
                    const newValue = currentStep + start;
                    if (onChange) {
                        onChange(newValue);
                    }
                }}
            ></span>
        )
    });


    const labelDescriptionMapper = () => {
        // Use 0 when value is undefined
        const valueToUse = value !== undefined ? value : 0;
        // Converting value to percentage but not multiplying by 100
        const valueToPercentage = (valueToUse / 100);
        // Multiplying by (labelDescriptionArray.length - 1)  then rounding it up to get the index of the labelDescriptionArray
        const indexToRenderDescriptionDynamically = Math.round(valueToPercentage * (labelDescriptionArray.length - 1));
        return (
            discrete ?
                <>
                    <span className='Advance-SliderBar-label-description'>
                        <span className='Advance-SliderBar-label-in-description'>
                            {labelDescriptionArray[(indexToRenderDescriptionDynamically)].label}
                        </span>
                        <span className='Advance-SliderBar-description'>
                            {labelDescriptionArray[(indexToRenderDescriptionDynamically)].description === '' ?
                                null
                                :
                                ':' + ' ' + labelDescriptionArray[(indexToRenderDescriptionDynamically)].description
                            }
                        </span>
                    </span>
                </>
                :
                null
        )
    };

    const handleMouseDownCombinedThumb = () => {
        setIsHoveredOrActive(true);
        handleMouseDown();
    }


    return (
        <div className='Advance-SliderBar-Wrapper'>
            <div className="Advance-SliderBar">
                <input
                    ref={sliderRef}
                    type="range"
                    min={start}
                    max={end}
                    className="Advance-SliderBar-track"
                    style={{ background: bg, color: `${settings.fill}` }}
                    value={value}
                    onChange={onSlide}
                    step={stepCalculator}
                />
                <div className='Advance-SliderBar-marks-wrapper'>
                    {marks && markForEachStep ?
                        marksForEachStepMapper
                        :
                        marks ?
                            marksMapper
                            :
                            null
                    }
                </div>
                <div className='Advance-SliderBar-custom-thumb-container'>
                    {/* Add ref={thumbRef} for access thumb in future */}
                    <span
                        data-testid='Advance-SliderBar-custom-thumb'
                        className='Advance-SliderBar-custom-thumb'
                        style={customThumbPositionLogicAndStyles()}
                        onMouseDown={handleMouseDownCombinedThumb}
                        onMouseEnter={() => setIsHoveredOrActive(true)}
                        onMouseLeave={() => setIsHoveredOrActive(false)} //Turn on if you want to hide tooltip on mouse leave
                    >
                        {
                            showValueTooltip ?
                                <span
                                    className={
                                        `Advance-SliderBar-custom-thumb-tooltip ${isHoveredOrActive ?
                                            'Advance-SliderBar-custom-thumb-tooltip-visible'
                                            :
                                            ''
                                        }`}
                                >
                                    {(Math.round(value)).toString()}
                                </span>
                                //This commented code is used to show the tooltip on mouse leave for debugging purposes
                                // <span
                                //     className='Advance-SliderBar-custom-thumb-tooltip Advance-SliderBar-custom-thumb-tooltip-visible'
                                // >
                                //     {(Math.round(value)).toString()}
                                // </span>
                                :
                                null
                        }
                    </span>
                </div>
                <div className="Advance-SliderBar-label-container">
                    {labelsMapper}
                </div>
                <div className="Advance-SliderBar-label-description-container">
                    {showDescription ?
                        labelDescriptionMapper()
                        :
                        null
                    }
                </div>
            </div>
        </div>
    )
}


export default SliderBar